import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  OwnerCreateBranchRequest,
  OwnerPatchBranchRequest,
} from '@naya/shared'
import { ownerApi } from '../ownerApi.js'
import type {
  CreateBranchResponse,
  ListBranchesResponse,
  PatchBranchResponse,
} from '../types.js'

export const branchesKey = ['owner', 'branches'] as const

/**
 * The tenant's branches. Reads the shared GET /api/branches route (owner +
 * staff roles allowed). Powers the dashboard's "setup incomplete" signal AND
 * the slice 4b Branches page.
 */
export function useBranches() {
  return useQuery<ListBranchesResponse>({
    queryKey: branchesKey,
    queryFn: () => ownerApi.get<ListBranchesResponse>('/branches'),
  })
}

/** Create a branch — the setup wizard's step 4. */
export function useCreateBranch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OwnerCreateBranchRequest) =>
      ownerApi.post<CreateBranchResponse>('/owner/branches', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: branchesKey })
    },
  })
}

/**
 * Slice 4b — patch name + lat/lng from the Branches page. Hits the shared
 * /api/branches/:id route extended in 4b to accept coords.
 */
export function usePatchBranch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { branchId: string; input: OwnerPatchBranchRequest }) =>
      ownerApi.patch<PatchBranchResponse>(
        `/branches/${args.branchId}`,
        args.input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: branchesKey })
    },
  })
}
